import { ComponentProps, FC, ReactNode } from "react";
import type { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

interface WidgetItemRowProps extends Omit<ComponentProps<"div">, "title"> {
  /** The grip takes the drag handle, so the rest of the row stays clickable. */
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  isDragging?: boolean;
  /** What kind of item this is (Kilde, Workspace-dokument, …). */
  kindBadge?: ReactNode;
  /** The item's title, or the rename field while it is being renamed. */
  title: ReactNode;
  /** One muted line under the title: where the item comes from. */
  sourceLine?: ReactNode;
  /** Whether a source follows its newest version or is locked to one (LAR-505). */
  versionChip?: ReactNode;
  /** The item's ⋮ menu. */
  menu?: ReactNode;
}

/**
 * One item in a list widget's editor (LAR-726). The layout is fixed — grip,
 * kind badge, title over source line, version chip, menu — and each part is a
 * slot, so new item kinds and the version chip fill it without reshaping it.
 */
const WidgetItemRow: FC<WidgetItemRowProps> = ({
  dragHandleProps,
  isDragging = false,
  kindBadge,
  title,
  sourceLine,
  versionChip,
  menu,
  className,
  ...rowProps
}) => (
  <div
    {...rowProps}
    data-slot="widget-item-row"
    className={cn(
      "flex w-full min-w-0 items-center gap-2 bg-background px-2 py-2 transition-colors",
      isDragging ? "rounded-lg opacity-90 shadow-md" : "hover:bg-muted/50",
      className
    )}
  >
    <span
      {...dragHandleProps}
      aria-label="Træk for at ændre rækkefølgen"
      className="flex shrink-0 cursor-grab items-center self-stretch text-muted-foreground active:cursor-grabbing"
    >
      <GripVertical className="h-4 w-4" aria-hidden="true" />
    </span>
    {kindBadge && <span className="shrink-0">{kindBadge}</span>}
    <div className="min-w-0 flex-1">
      <div className="flex min-w-0 items-center gap-2">{title}</div>
      {sourceLine && (
        <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {sourceLine}
        </div>
      )}
    </div>
    {versionChip && <span className="shrink-0">{versionChip}</span>}
    {menu && <span className="shrink-0">{menu}</span>}
  </div>
);

export default WidgetItemRow;
